import React, { useState, useEffect, useMemo, useCallback } from 'react';
import cx from 'classnames';
import {
  autoUpdate,
  flip,
  FloatingFocusManager,
  hide,
  offset,
  shift,
  useFloating,
  useDismiss,
  useRole,
  useInteractions,
} from '@floating-ui/react';
import type { FlipOptions, Placement } from '@floating-ui/react';
import { Transition } from '@headlessui/react';
import moment from 'moment';

import { Calendar, CalendarTime, Time } from '../../Icons';
import Portal from '../../HOC/Portal';
import InputText from '../InputText';
import Select from './Select';

interface DatePickerPropsT
  extends Omit<
    React.InputHTMLAttributes<HTMLInputElement>,
    'onChange' | 'value' | 'size'
  > {
  value?: Date | string;
  onChange?: (date: Date) => void;
  className?: string;
  inputClassName?: string;
  iconClassName?: string;
  popoverClassName?: string;
  onlyDate?: boolean;
  label?: string;
  placeholder?: string;
  placement?: Placement;
  popupFilpOption?: FlipOptions;
  helperText?: string;
  isError?: boolean;
  size?: 'sm' | 'md' | 'lg';
  disabled?: boolean;
  required?: boolean;
  readOnly?: boolean;
  variant?: 'filled' | 'standard';
  portalTarget?: Element | DocumentFragment | null;
}

const DateTimePicker: React.FC<DatePickerPropsT> = ({
  value,
  onChange = () => {},
  className,
  inputClassName,
  iconClassName,
  popoverClassName,
  onlyDate,
  placement,
  label,
  placeholder,
  size = 'sm',
  helperText,
  isError,
  disabled,
  required,
  readOnly,
  popupFilpOption = { padding: 10 },
  variant = 'filled',
  portalTarget,
  ...props
}) => {
  const [picker, setPicker] = useState<'date' | 'time' | undefined>(undefined);

  const { refs, floatingStyles, context } = useFloating({
    placement: placement,
    whileElementsMounted: autoUpdate,
    open: picker !== undefined,
    onOpenChange: () => setPicker(undefined),
    middleware: [
      offset(1),
      flip(popupFilpOption),
      shift({ padding: 5 }),
      hide(),
    ],
  });
  const dismiss = useDismiss(context);
  const role = useRole(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([
    dismiss,
    role,
  ]);

  const [controlledValue, setControlledValue] = useState<Date>(
    value ? new Date(value) : undefined
  );
  useEffect(
    () => setControlledValue(value ? new Date(value) : undefined),
    [value]
  );

  const formattedDate = useMemo(
    () =>
      controlledValue
        ? {
            date: moment(controlledValue).format('MM/DD/YYYY'),
            time: moment(controlledValue).format('hh:mm a'),
          }
        : undefined,
    [controlledValue]
  );

  const handleCancel = useCallback(() => {
    setControlledValue(value ? new Date(value) : undefined);

    setPicker(undefined);
  }, [value]);

  const handleChange = (value: Date) => {
    setControlledValue(value);
    onChange(value);
  };

  const handleConfirm = (value: Date) => {
    handleChange(value);
    setPicker(undefined);
  };

  const Icon = useMemo(() => (onlyDate ? Calendar : CalendarTime), [onlyDate]);

  return (
    <div className={cx('datetime-field', size, className && className)}>
      <input
        type="hidden"
        readOnly
        value={controlledValue?.toString() || ''}
        name={props.name}
      />
      {/* desktop view */}
      <div
        className="max-md:hidden w-full"
        ref={refs.setReference}
        data-state={open ? 'open' : 'closed'}
        {...getReferenceProps()}
      >
        {variant === 'standard' ? (
          <p
            className={cx('cursor-pointer input-text leading-loose', size)}
            onClick={() => {
              if (!readOnly && !disabled) setPicker('date');
            }}
          >
            {formattedDate
              ? `${formattedDate.date} ${` `} ${
                  !onlyDate ? formattedDate.time : ''
                }`
              : ''}
          </p>
        ) : (
          <InputText
            required={required}
            disabled={disabled}
            isError={isError}
            helperText={helperText}
            size={size}
            inputClassName={inputClassName}
            className="w-full"
            label={label}
            placeholder={
              placeholder ?? (onlyDate ? 'MM/DD/YYYY' : 'MM/DD/YYYY hh:mm aa')
            }
            readOnly
            value={
              formattedDate
                ? `${formattedDate.date} ${` `} ${
                    !onlyDate ? formattedDate.time : ''
                  }`
                : ''
            }
            rightIcon={
              <Icon
                className={cx(
                  disabled ? 'opacity-50' : 'fill-black cursor-pointer',
                  iconClassName && iconClassName
                )}
                width={18}
                height={18}
                onClick={() => {
                  if (!readOnly && !disabled) setPicker('date');
                }}
              />
            }
            onClick={() => {
              if (!readOnly && !disabled) setPicker('date');
            }}
          />
        )}
      </div>

      {/* mobile view */}
      <div className="inline-flex gap-2 items-start w-full md:hidden">
        {/* mobile date picker */}
        <InputText
          required={required}
          disabled={disabled}
          isError={isError}
          helperText={helperText}
          size={size}
          inputClassName={cx('date-input', inputClassName && inputClassName)}
          className="flex-1"
          label="Date"
          placeholder="MM/DD/YYYY"
          readOnly
          value={formattedDate?.date ?? ''}
          rightIcon={
            <Calendar
              className={cx(
                disabled ? 'opacity-50' : 'fill-black cursor-pointer',
                iconClassName && iconClassName
              )}
              width={18}
              height={18}
              onClick={() => {
                if (!readOnly && !disabled) setPicker('date');
              }}
            />
          }
          onClick={() => {
            if (!readOnly && !disabled) setPicker('date');
          }}
        />
        {/* mobile time picker */}
        {!onlyDate && (
          <InputText
            required={required}
            disabled={disabled}
            isError={isError}
            size={size}
            inputClassName={cx('time-input', inputClassName && inputClassName)}
            className="flex-1"
            label="Time"
            placeholder="hh:mm aa"
            readOnly
            value={formattedDate?.time ?? ''}
            rightIcon={
              <Time
                className={cx(
                  disabled ? 'opacity-50' : 'fill-black cursor-pointer',
                  iconClassName && iconClassName
                )}
                width={18}
                height={18}
                onClick={() => {
                  if (!readOnly && !disabled)
                    setPicker(controlledValue ? 'time' : 'date');
                }}
              />
            }
            onClick={() => {
              if (!readOnly && !disabled)
                setPicker(controlledValue ? 'time' : 'date');
            }}
          />
        )}
      </div>

      <Portal portalTarget={portalTarget}>
        {picker !== undefined && (
          <div className="md:hidden fixed inset-0 bg-[#E3E3E3] bg-opacity-75 w-full h-full" />
        )}
        <FloatingFocusManager context={context} modal={false}>
          <Transition
            appear
            show={picker !== undefined}
            as={React.Fragment}
            enter="ease-in-out max-md:duration-500 max-md:transition"
            enterFrom="opacity-0 max-md:scale-95"
            enterTo="opacity-100 max-md:scale-100"
            leave="ease-out max-md:transition max-md:duration-500"
            leaveTo="opacity-0 max-md:scale-95"
          >
            <div
              className={cx(
                'date-time-popover',
                {
                  'md:invisible': context.middlewareData?.hide?.referenceHidden,
                },
                popoverClassName && popoverClassName
              )}
              ref={refs.setFloating}
              style={{
                ...floatingStyles,
                zIndex: 999,
                outline: 'none',
              }}
              {...getFloatingProps()}
            >
              <Select
                value={controlledValue}
                onlyDate={onlyDate}
                picker={picker}
                onChange={handleChange}
                onConfirm={handleConfirm}
                onCancel={handleCancel}
              />

              {/* <div className="hidden md:flex flex-row justify-end gap-2.5 p-[15px]">
                <Button
                  className="flex-1 font-bold"
                  onClick={() => {
                    // if (pickYear) {
                    //   setTemp_Y_M({ ...formattedValue });
                    //   setPickYear(false);
                    // } else onCancel();
                  }}
                >
                  Cancel
                </Button>
                <Button
                  className="flex-1 font-bold"
                  onClick={() => {
                    // if (pickYear) {
                    //   setFormattedValue((value) => ({
                    //     ...value,
                    //     year: temp_Y_M.year,
                    //   }));
                    //   setPickYear(false);
                    // } else onConfirm();
                  }}
                  variant="solid"
                  disabled={value === undefined}
                >
                  Ok
                </Button>
              </div> */}
            </div>
          </Transition>
        </FloatingFocusManager>
      </Portal>
    </div>
  );
};

DateTimePicker.defaultProps = {
  placement: 'bottom-start',
};

export default DateTimePicker;
